import { motion } from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

const lines = [
  "Helping product managers ship with fewer loose threads.",
  "Helping sales people get more of their day back.",
  "Helping parents juggle work and home without dropping balls.",
  "Helping founders stay on top of what matters.",
];

function PresenceRings() {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      aria-hidden="true"
    >
      {[1, 2, 3].map((ring) => (
        <motion.div
          key={ring}
          className="absolute rounded-full border border-teamie-accent/20"
          style={{
            width: `${ring * 140}px`,
            height: `${ring * 140}px`,
          }}
          animate={{
            scale: [1, 1.06, 1],
            opacity: [0.35 - ring * 0.08, 0.55 - ring * 0.08, 0.35 - ring * 0.08],
          }}
          transition={{
            duration: 4 + ring,
            repeat: Infinity,
            ease: "easeInOut",
            delay: ring * 0.4,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-teamie-bg via-teamie-surface to-teamie-bg" />
        <div className="hero-mesh absolute inset-0 opacity-60" />
        <PresenceRings />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[calc(100vh-5rem)] py-12 lg:py-0">
          <div className="lg:col-span-7 text-left">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-mono text-xs tracking-[0.2em] uppercase text-teamie-text-secondary mb-8"
              data-testid="hero-overline"
            >
              Digital twin for work and life
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05] font-light text-teamie-text mb-8 text-balance"
              data-testid="hero-headline"
            >
              Your work stays moving.
              <br />
              <span className="text-teamie-accent italic">Your life gets room.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="font-body text-base sm:text-lg text-teamie-text-secondary max-w-xl mb-6 leading-relaxed"
              data-testid="hero-subtext"
            >
              teamie is a digital twin that knows how you work and how you live. It handles
              follow-ups, remembers context, and shows up where you cannot, from client
              threads to school runs.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="mb-10 h-8 overflow-hidden"
              data-testid="hero-role-rotator"
            >
              <motion.div
                animate={{ y: [0, -32, -64, -96, 0] }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="font-body text-sm text-teamie-green"
              >
                {lines.map((line) => (
                  <p key={line} className="h-8 flex items-center">
                    {line}
                  </p>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="flex flex-wrap gap-4"
            >
              <button
                type="button"
                data-testid="hero-waitlist-cta"
                onClick={() => scrollToSection("waitlist", { focusEmail: true })}
                className="px-8 py-3 rounded-full border border-teamie-text text-teamie-text text-sm font-body font-medium hover:bg-teamie-accent hover:text-teamie-bg hover:border-teamie-accent transition-colors duration-300"
              >
                Join Waitlist
              </button>
              <button
                type="button"
                data-testid="hero-explore-link"
                onClick={() => scrollToSection("roles")}
                className="px-8 py-3 rounded-full text-teamie-text-secondary text-sm font-body hover:text-teamie-accent transition-colors duration-300"
              >
                See who it helps
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="relative border border-teamie-border rounded-lg p-10 bg-teamie-bg/50 backdrop-blur-sm">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-teamie-text-secondary mb-6">
                One twin, two sides of your day
              </p>
              <div className="space-y-6">
                {[
                  {
                    label: "At work",
                    hint: "Meetings, deals, roadmaps, and the inbox you never quite clear.",
                  },
                  {
                    label: "At home",
                    hint: "Appointments, reminders, and the small things that slip when work runs long.",
                  },
                  {
                    label: "In your voice",
                    hint: "Replies and decisions that sound like you, not a generic assistant.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }}
                    className="group border-l-2 border-teamie-accent/30 pl-5 hover:border-teamie-accent transition-colors duration-300"
                  >
                    <p className="font-heading text-xl text-teamie-text mb-1">{item.label}</p>
                    <p className="font-body text-sm text-teamie-text-secondary">{item.hint}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        data-testid="scroll-indicator"
      >
        <div className="w-[1px] h-12 bg-teamie-text-secondary/30 relative overflow-hidden">
          <motion.div
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-x-0 top-0 h-1/2 bg-teamie-accent/60"
          />
        </div>
      </motion.div>
    </section>
  );
}
